/**
 * PhotoStore - Single Source of Truth for Profile Photos
 *
 * V258.2: State-aware photo storage with explicit state machine
 * - Separate storage for preview blobs vs uploaded remote URLs
 * - State machine: previewing → uploading → uploaded
 * - Atomic operations with verification
 *
 * Architecture:
 * - Cache key: `profile_photo:${userId}` (stable, never changes)
 * - Cache Storage (primary) -> persistentCache/localStorage (fallback) -> memory (fallback)
 * - Always overwrites same key (no URL-based keys)
 *
 * V250: Only PhotoStore.setBlob() is allowed to create object URLs for profile photos
 */

import { createProfileUrlForKey, revokeProfileUrlForKey, getProfileUrlForKey } from './objectUrl';
import {
  resolveProfilePhotoCacheUrl,
  setProfilePhotoCachePayload,
} from './profilePhotoKeys';

const CACHE_NAME = 'profile-photo-v2';
const STABLE_KEY_PREFIX = 'profile_photo:';

// V258.2: State machine
type PhotoState = 'previewing' | 'uploading' | 'uploaded';
const photoStates = new Map<string, PhotoState>();
const previewBlobs = new Map<string, { blobUrl: string; revoked: boolean }>();
const uploadedPhotos = new Map<string, string>(); // userId → remote URL

// V250: Memory fallback (last resort)
const memoryCache = new Map<string, { blob: Blob; objectUrl: string }>();

// V250: Prevent duplicate loads (deduplication)
const inFlightLoads = new Map<string, Promise<string | null>>(); // key -> load promise

// V250: Note: objectUrlByKey is now managed by objectUrl.ts (centralized manager)

// Event listeners for PhotoStore updates (notify components when cache changes)
const listeners = new Set<(userId: string) => void>();

/**
 * Subscribe to PhotoStore updates
 * Returns unsubscribe function
 */
export function subscribeToPhotoUpdates(listener: (userId: string) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Notify listeners of PhotoStore update
 */
function notifyListeners(userId: string): void {
  listeners.forEach(listener => {
    try {
      listener(userId);
    } catch (error) {
      console.warn('⚠️ PhotoStore: Listener error:', error);
    }
  });
}

/**
 * Get stable cache key for userId
 */
function getCacheKey(userId: string): string {
  return `${STABLE_KEY_PREFIX}${userId}`;
}

/**
 * V258.2: Set preview blob (state: none → PREVIEWING)
 */
export async function v258SetPreview(userId: string, blob: Blob): Promise<string> {
  console.log('🔵 V258.2: Setting preview blob for', userId);

  // Clear old preview
  const old = previewBlobs.get(userId);
  if (old && !old.revoked) {
    URL.revokeObjectURL(old.blobUrl);
  }

  const blobUrl = URL.createObjectURL(blob);
  previewBlobs.set(userId, { blobUrl, revoked: false });
  photoStates.set(userId, 'previewing');

  console.log('✅ V258.2: Preview set | State: PREVIEWING');
  return blobUrl;
}

/**
 * V258.2: Mark upload in progress (state: PREVIEWING → UPLOADING)
 */
export function v258StartUpload(userId: string): void {
  const state = photoStates.get(userId);
  if (state !== 'previewing') {
    console.warn(`⚠️ V258.2: Invalid state transition - expected PREVIEWING, got ${state}`);
  }
  photoStates.set(userId, 'uploading');
  console.log('🔵 V258.2: State → UPLOADING');
}

/**
 * V258.2: Set uploaded photo ATOMIC (state: UPLOADING → UPLOADED)
 * Clears blob, sets remote URL, transitions state, and verifies
 */
export async function v258SetUploaded(userId: string, remoteUrl: string): Promise<void> {
  console.log('🎯 V258.2: ========================================');
  console.log('🎯 V258.2: Setting uploaded photo for', userId);
  console.log('🎯 V258.2: Remote URL:', remoteUrl);

  if (!remoteUrl.startsWith('http')) {
    throw new Error('V258.2: Invalid URL - not remote');
  }

  const state = photoStates.get(userId);
  if (state !== 'uploading') {
    console.warn(`⚠️ V258.2: Invalid state - expected UPLOADING, got ${state}`);
  }

  // ATOMIC: Clear blob + Set remote + Transition state
  const preview = previewBlobs.get(userId);
  if (preview && !preview.revoked) {
    URL.revokeObjectURL(preview.blobUrl);
    preview.revoked = true;
  }
  previewBlobs.delete(userId);
  console.log('🗑️ V258.2: Blob cleared');

  uploadedPhotos.set(userId, remoteUrl);
  photoStates.set(userId, 'uploaded');
  console.log('✅ V258.2: State → UPLOADED');

  // VERIFY
  const verify = await getPhoto(userId);
  const isRemote = verify?.startsWith('http');
  const isBlob = verify?.startsWith('blob:');

  console.log('🔍 V258.2: Verification:', {
    url: verify?.substring(0, 50),
    isRemote,
    isBlob,
    correct: isRemote && !isBlob
  });

  if (isBlob || !isRemote) {
    throw new Error('❌ V258.2: VERIFICATION FAILED - not returning remote URL!');
  }

  console.log('✅ V258.2: Verification PASSED');
  console.log('🎯 V258.2: ========================================');
}

/**
 * V258.2: Get photo from cache (STATE-AWARE)
 * PRIORITY: uploaded state → remote URL, previewing/uploading → blob URL
 */
export async function getPhoto(userId: string): Promise<string | null> {
  const state = photoStates.get(userId);

  console.log(`🔵 V258.2: getPhoto for ${userId} | State: ${state || 'none'}`);

  // PRIORITY 1: If UPLOADED, return remote URL ONLY
  if (state === 'uploaded') {
    const remoteUrl = uploadedPhotos.get(userId);
    if (remoteUrl) {
      console.log('✅ V258.2: Returning remote URL (UPLOADED state)');
      return remoteUrl;
    }
  }

  // PRIORITY 2: If PREVIEWING/UPLOADING, return blob
  if (state === 'previewing' || state === 'uploading') {
    const preview = previewBlobs.get(userId);
    if (preview && !preview.revoked) {
      console.log('🔵 V258.2: Returning preview blob');
      return preview.blobUrl;
    }
  }

  // PRIORITY 3: Check uploadedPhotos as fallback
  const uploaded = uploadedPhotos.get(userId);
  if (uploaded) {
    console.log('✅ V258.2: Returning remote URL (fallback)');
    return uploaded;
  }

  // PRIORITY 4: Try legacy cache (backward compat)
  try {
    const legacyUrl = await resolveProfilePhotoCacheUrl(userId);
    if (legacyUrl?.startsWith('http')) {
      console.log('ℹ️ V258.2: Returning legacy remote URL');
      return legacyUrl;
    }
  } catch (error) {
    // Silent fallback
  }

  console.log('ℹ️ V258.2: No photo found');
  return null;
}

/**
 * V250: Store blob in cache (stable key by userId) with object URL reuse
 * Overwrites existing entry - this ensures UI updates instantly
 * Returns objectURL for immediate use (reused if same blob already cached)
 */
export async function setBlob(userId: string, blob: Blob, mimeType: string = 'image/webp'): Promise<string> {
  const key = getCacheKey(userId);
  
  // V250: Use centralized object URL manager (revokes old URL automatically)
  const objectUrl = createProfileUrlForKey(key, blob);
  
  // Clean up old memory entry if exists (sync state)
  const oldMemoryEntry = memoryCache.get(key);
  if (oldMemoryEntry && oldMemoryEntry.objectUrl !== objectUrl) {
    // Memory cache had different URL, update it (URL was already revoked by createProfileUrlForKey)
    // No need to revoke here, centralized manager handles it
  }
  
  // Store in memory (always available)
  memoryCache.set(key, { blob, objectUrl });
  
  try {
    // Store in Cache Storage (best persistence)
    if ('caches' in window) {
      const cache = await caches.open(CACHE_NAME);
      const response = new Response(blob, {
        headers: {
          'Content-Type': mimeType,
          'Cache-Control': 'max-age=31536000',
        },
      });
      await cache.put(key, response);
      console.log('✅ PhotoStore: Stored blob in Cache Storage:', key);
    }
  } catch (error) {
    console.warn('⚠️ PhotoStore: Cache Storage write failed (using memory only):', error);
  }

  // V253: DO NOT write blob cache payload here - blob is only for instant preview
  // Canonical cache should always be {kind:'url'} written by commitProfilePhotoUpdate
  // This prevents blob entries from being selected when blob is dead
  // (Blob payloads are only written temporarily during upload, then replaced by URL payload)

  if (import.meta.env.DEV) {
    console.log('✅ V250 PhotoStore: Blob stored and ready (objectUrl created):', key);
  }
  
  // V250: Notify listeners that photo was updated (ONCE, not per component)
  notifyListeners(userId);
  
  return objectUrl;
}

/**
 * Store remote URL as metadata (optional, for reference)
 * Does NOT fetch the image - use setBlob for actual caching
 */
export async function setRemoteUrl(userId: string, url: string): Promise<void> {
  const key = getCacheKey(userId);
  
  try {
    setProfilePhotoCachePayload(userId, {
      kind: 'url',
      url,
      timestamp: Date.now(),
      ttl: 24 * 60 * 60 * 1000,
    });
    console.log('✅ PhotoStore: Remote URL stored as structured payload:', key);
  } catch (error) {
    console.warn('⚠️ PhotoStore: Failed to store remote URL:', error);
  }
}

/**
 * V257: Clear preview blob (blob cache only, keeps remote URL)
 * Use this after upload success to clear blob while preserving remote URL
 */
export async function clearPreview(userId: string): Promise<void> {
  const key = getCacheKey(userId);
  
  console.log(`🔄 V257 PhotoStore: Clearing preview blob for: ${key}`);
  
  // Revoke object URL (blob becomes invalid)
  revokeProfileUrlForKey(key);
  
  // Clear memory cache (blob only)
  memoryCache.delete(key);
  
  try {
    // Delete blob from Cache Storage (but keep remote URL in profilePhotoKeys)
    if ('caches' in window) {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(key);
      console.log(`✅ V257 PhotoStore: Blob cleared from Cache Storage: ${key}`);
    }
  } catch (error) {
    console.warn('⚠️ V257 PhotoStore: Cache Storage delete failed:', error);
  }
  
  // V257: DO NOT delete remote URL from profilePhotoKeys - that's the canonical source
  // Remote URL is stored via setRemoteUrl() and should persist after upload success
  
  console.log(`✅ V257 PhotoStore: Preview blob cleared (remote URL preserved): ${key}`);
}

/**
 * V250: Invalidate photo cache for userId (deletes stable key and revokes object URL)
 * WARNING: This clears BOTH blob AND remote URL - use clearPreview() if you only want to clear blob
 */
export async function invalidate(userId: string): Promise<void> {
  const key = getCacheKey(userId);
  
  // V250: Use centralized object URL manager to revoke (handles URL revocation)
  revokeProfileUrlForKey(key);
  
  // Clean up memory cache (URL already revoked by centralized manager)
  memoryCache.delete(key);
  
  try {
    // Delete from Cache Storage
    if ('caches' in window) {
      const cache = await caches.open(CACHE_NAME);
      await cache.delete(key);
    }
  } catch (error) {
    console.warn('⚠️ PhotoStore: Cache Storage delete failed:', error);
  }
  
  try {
    // Delete from persistentCache
    const { persistentCache } = await import('./persistentCache');
    persistentCache.delete(key);
  } catch (error) {
    console.warn('⚠️ PhotoStore: persistentCache delete failed:', error);
  }
  
  // V257: Remote URL in profilePhotoKeys is left as-is (no delete API); next setRemoteUrl overwrites.

  console.log('✅ PhotoStore: Invalidated cache for:', key);
}

/**
 * Fetch remote URL and cache as blob (stable key)
 * Useful for warming cache from DB Cloudinary URL
 */
export async function fetchAndCache(userId: string, url: string): Promise<string | null> {
  try {
    console.log('📥 PhotoStore: Fetching and caching:', url.substring(0, 80));
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }
    
    const blob = await response.blob();
    const mimeType = blob.type || 'image/webp';
    
    const objectUrl = await setBlob(userId, blob, mimeType);
    
    // Also store remote URL as metadata
    await setRemoteUrl(userId, url);
    
    console.log('✅ PhotoStore: Fetched and cached successfully');
    return objectUrl;
  } catch (error) {
    console.error('❌ PhotoStore: Fetch and cache failed:', error);
    return null;
  }
}

